import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, Plus, LogIn, ChevronDown } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { startActivityCleanup, stopActivityCleanup, startTerminalActivityTracking, stopTerminalActivityTracking } from '../../stores/projectActivityStore';
import { useBackend } from '../../services/backend';
import { useMachineStore } from '../../stores/machineStore';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useGroupStore } from '../../stores/groupStore';
import { isWeb } from '../../lib/platform';
import {
  sortProjects,
  partitionByActive,
  groupByTeam,
  isProjectActive,
  openTerminalPaths,
  PROJECT_DND_MIME,
  UNGROUPED_ID,
} from '../../lib/projectSort';
import { getProjectGroupId, setProjectGroupId } from '../../lib/projectSettings';
import { useTerminalStore } from '../../stores/terminalStore';
import type { Project } from '../../types/tauri';
import MachineSelector from '../MachineSelector';
import SortControl from '../SortControl';
import ProjectActivityDot from '../ProjectActivityDot';
import TeamContextMenu from './TeamContextMenu';

/**
 * ProjectsPanel — renders project list content inside a flexlayout border panel.
 * No fixed positioning or collapse toggle (flexlayout manages those).
 *
 * When the user has defined Teams (project groups), projects are shown grouped
 * by team — each team a collapsible section with Active/Inactive within it —
 * and a project can be reassigned by dragging it onto a team header or via the
 * row's right-click menu. With no teams defined the panel falls back to the
 * original flat Active/Inactive view, so the feature is invisible until used.
 */
export default function ProjectsPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const projects = useProjectStore((state) => state.projects);
  const loadProjects = useProjectStore((state) => state.loadProjects);
  const updateProject = useProjectStore((state) => state.updateProject);
  const sortMode = useUIStore((state) => state.projectSortMode);
  const sortDir = useUIStore((state) => state.projectSortDir);
  const inactiveExpanded = useUIStore((state) => state.inactiveGroupExpanded);
  const toggleInactiveGroup = useUIStore((state) => state.toggleInactiveGroup);
  const collapsedTeams = useUIStore((state) => state.collapsedTeams);
  const toggleTeamCollapsed = useUIStore((state) => state.toggleTeamCollapsed);
  const teamFilter = useUIStore((state) => state.teamFilter);
  const setTeamFilter = useUIStore((state) => state.setTeamFilter);
  const groups = useGroupStore((state) => state.groups);
  const loadGroups = useGroupStore((state) => state.loadGroups);

  // Re-render (and re-sort) when a terminal opens/closes so the "active" sort
  // and the per-project dots stay current.
  useTerminalStore((state) => state.sessions);

  const backend = useBackend();

  const connectionState = useMachineStore((state) => state.connectionState);
  const selectedMachineId = useMachineStore((state) => state.selectedMachineId);
  const { isAuthenticated, setShowAuthDialog } = useAuthStore();

  // Drag-and-drop + context-menu state for team assignment.
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ projectId: string; x: number; y: number } | null>(null);

  const webMode = isWeb();
  const hasSelectedRealMachine = webMode ? selectedMachineId !== null : true;

  const currentProjectId = location.pathname.startsWith('/workspace/')
    ? location.pathname.split('/')[2]
    : null;

  useEffect(() => {
    loadProjects();
    void loadGroups();
    startActivityCleanup();
    return () => stopActivityCleanup();
  }, [loadProjects, loadGroups]);

  // Wire terminal output to activity indicators
  useEffect(() => {
    startTerminalActivityTracking(backend);
    return () => stopTerminalActivityTracking();
  }, [backend]);

  // Keep the ACTIVE project in view. Switching projects navigates to a new
  // /workspace/:id, which remounts this panel and resets the scroll container
  // back to the top — jarring on a long list. Instead, scroll the selected
  // project into view. block:'nearest' is a no-op when it's already visible, so
  // it never yanks the list around; rAF lets the flexlayout panel settle first.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));
    return () => cancelAnimationFrame(raf);
  }, [currentProjectId, projects.length]);


  const handleProjectClick = (projectId: string) => navigate(`/workspace/${projectId}`);

  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  const handleProjectsClick = () => {
    setCurrentProject(null);
    navigate('/');
  };

  const handleAddProject = () => {
    if (webMode && !hasSelectedRealMachine) return;
    useUIStore.getState().openAddProjectDialog();
  };

  const hasTeams = groups.length > 0;

  // Assign (or clear) a project's team, persisting through the project's
  // settings blob. No-op when it's already in that team.
  const assignToTeam = async (projectId: string, teamId: string | null) => {
    const p = projects.find((x) => x.id === projectId);
    if (!p) return;
    if (getProjectGroupId(p.settings) === teamId) return;
    try {
      await updateProject(projectId, { settings: setProjectGroupId(p.settings, teamId) });
    } catch {
      /* store logs the error; the list re-reads on next load */
    }
  };

  const onDropToTeam = (e: React.DragEvent, teamId: string | null) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData(PROJECT_DND_MIME);
    if (id) void assignToTeam(id, teamId);
  };

  // Color classes for project colors
  const colorClasses: Record<string, { bg: string; text: string; border: string }> = {
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500' },
    green: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' },
    orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500' },
    red: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' },
    yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500' },
    cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500' },
    indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500' },
    teal: { bg: 'bg-teal-500/20', text: 'text-teal-400', border: 'border-teal-500' },
  };

  const openPaths = openTerminalPaths();

  const renderProjectRow = (project: Project) => {
    const projectColor = project.color && colorClasses[project.color] ? colorClasses[project.color] : null;
    const isActive = currentProjectId === project.id;
    return (
      <button
        key={project.id}
        ref={isActive ? activeRef : undefined}
        draggable={hasTeams}
        onDragStart={hasTeams ? (e) => {
          e.dataTransfer.setData(PROJECT_DND_MIME, project.id);
          e.dataTransfer.effectAllowed = 'move';
        } : undefined}
        onContextMenu={hasTeams ? (e) => { e.preventDefault(); setMenu({ projectId: project.id, x: e.clientX, y: e.clientY }); } : undefined}
        onClick={() => handleProjectClick(project.id)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? projectColor
              ? `${projectColor.bg} ${projectColor.text} border-l-4 ${projectColor.border}`
              : 'bg-primary text-primary-content'
            : projectColor
              ? `hover:${projectColor.bg} border-l-4 border-transparent hover:${projectColor.border}`
              : 'hover:bg-base-300'
        }`}
        title={hasTeams ? `${project.name}  ·  drag to a team, or right-click to move` : project.name}
      >
        <span className="relative inline-flex flex-shrink-0">
          {project.icon?.startsWith('data:') ? (
            <img src={project.icon} alt="" className="w-5 h-5 rounded object-cover" />
          ) : project.icon ? (
            <span className="text-lg w-5 h-5 flex items-center justify-center">{project.icon}</span>
          ) : (
            <FolderOpen className={`w-5 h-5 ${isActive && projectColor ? projectColor.text : ''}`} />
          )}
          <ProjectActivityDot projectPath={project.root_path} />
        </span>
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium truncate">{project.name}</div>
          <div className={`text-xs truncate ${
            isActive ? projectColor ? `${projectColor.text}/70` : 'text-primary-content/70' : 'text-base-content/60'
          }`}>
            {project.root_path}
          </div>
        </div>
      </button>
    );
  };

  // Active (emerald) header + rows, then a collapsible Inactive subsection.
  // Shared by the flat (no-teams) view and each team section.
  const renderActiveInactive = (active: Project[], inactive: Project[], opts?: { indent?: boolean }) => (
    <>
      {active.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-3 pt-2 pb-1">
            <span className="w-3.5 flex-shrink-0" />
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-500/80">
              Active ({active.length})
            </span>
          </div>
          {active.map(renderProjectRow)}
        </div>
      )}
      {inactive.length > 0 && (
        <div className="space-y-1">
          <button
            onClick={toggleInactiveGroup}
            className="flex items-center gap-2 px-3 pt-2 pb-1 w-full text-left hover:bg-base-300/40 rounded-lg transition-colors"
            title={inactiveExpanded ? 'Collapse inactive' : 'Expand inactive'}
          >
            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-base-content/40 transition-transform ${inactiveExpanded ? '' : '-rotate-90'}`} />
            <span className="w-1.5 h-1.5 rounded-full bg-base-content/30" />
            <span className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
              Inactive ({inactive.length})
            </span>
          </button>
          {inactiveExpanded && inactive.map(renderProjectRow)}
        </div>
      )}
      {active.length === 0 && inactive.length === 0 && opts?.indent && (
        <div className="px-3 py-2 text-xs text-base-content/40 italic">Drag a project here</div>
      )}
    </>
  );

  // Sort within mode+direction once; both views reuse it.
  const sortedProjects = sortProjects(projects, sortMode, sortDir);

  // Team buckets (every team in order, then an Ungrouped catch-all). Computed
  // once so the grouped view and the single-team filter both reuse it.
  const buckets = hasTeams
    ? groupByTeam(sortedProjects, groups, (p) => isProjectActive(p, openPaths))
    : [];

  // A persisted filter can dangle if its team was deleted — treat that as "All".
  const filterValid =
    teamFilter === null ||
    teamFilter === UNGROUPED_ID ||
    groups.some((g) => g.id === teamFilter);
  const effectiveFilter = filterValid ? teamFilter : null;
  const filteredBucket =
    effectiveFilter === null
      ? null
      : buckets.find((b) => (b.group?.id ?? UNGROUPED_ID) === effectiveFilter) ?? {
          group: groups.find((g) => g.id === effectiveFilter) ?? null,
          active: [],
          inactive: [],
        };

  return (
    <div className="flex flex-col h-full bg-base-200 overflow-hidden">
      {/* Machine Selector */}
      {(webMode || connectionState === 'authenticated') && (
        <MachineSelector isCollapsed={false} />
      )}

      {/* Sign In prompt for web mode */}
      {webMode && !isAuthenticated && (
        <div className="px-4 py-2 border-b border-base-300">
          <button onClick={() => setShowAuthDialog(true)} className="btn btn-primary btn-sm w-full gap-2">
            <LogIn className="w-4 h-4" />Sign In
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* Projects header with + button */}
        <div className="flex items-center justify-between px-3 py-2">
          <button
            onClick={handleProjectsClick}
            className="flex items-center gap-2 hover:text-primary transition-colors"
            title="All Projects"
          >
            <FolderOpen className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">Projects</span>
          </button>
          <div className={webMode && !hasSelectedRealMachine ? 'tooltip tooltip-left' : ''} data-tip="Select a machine first">
            <button
              onClick={handleAddProject}
              disabled={webMode && !hasSelectedRealMachine}
              className={`btn btn-ghost btn-xs btn-square ${webMode && !hasSelectedRealMachine ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Add project"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Sort control */}
        {projects.length > 1 && (
          <div className="px-3 pb-2">
            <SortControl />
          </div>
        )}

        {/* Team filter — narrow the list to one team for screen-sharing, or
            "All Projects" for the full grouped view. Only shown once teams
            exist. */}
        {hasTeams && (
          <div className="px-3 pb-2">
            <select
              value={effectiveFilter ?? ''}
              onChange={(e) => setTeamFilter(e.target.value === '' ? null : e.target.value)}
              className="select select-sm select-bordered w-full text-xs"
              title="Filter projects by team"
            >
              <option value="">All Projects</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              <option value={UNGROUPED_ID}>Ungrouped</option>
            </select>
          </div>
        )}

        {/* No teams defined → original flat Active/Inactive view. */}
        {!hasTeams && (() => {
          const { active, inactive } = partitionByActive(sortedProjects);
          return <div className="space-y-1">{renderActiveInactive(active, inactive)}</div>;
        })()}

        {/* Teams defined + a single team selected → flat Active/Inactive for
            just that team (one layer, no team headers) — presentation mode. */}
        {hasTeams && filteredBucket && (
          <div className="space-y-1">
            {filteredBucket.active.length === 0 && filteredBucket.inactive.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-base-content/50">
                No projects in {filteredBucket.group?.name ?? 'Ungrouped'} yet
              </div>
            ) : (
              renderActiveInactive(filteredBucket.active, filteredBucket.inactive)
            )}
          </div>
        )}

        {/* Teams defined, "All Projects" → one collapsible section per team
            (Active/Inactive within), then an Ungrouped catch-all. Each header
            is a drop target. */}
        {hasTeams && !filteredBucket && (
          <div className="space-y-1">
            {buckets.map((bucket) => {
              const key = bucket.group?.id ?? UNGROUPED_ID;
              const name = bucket.group?.name ?? 'Ungrouped';
              const color = bucket.group?.color ?? null;
              const count = bucket.active.length + bucket.inactive.length;
              const collapsed = collapsedTeams.includes(key);
              const dropping = dragOverKey === key;
              return (
                <div
                  key={key}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey((k) => (k === key ? null : k)); }}
                  onDrop={(e) => onDropToTeam(e, bucket.group?.id ?? null)}
                  className={`rounded-lg transition-colors ${dropping ? 'ring-2 ring-primary/60 bg-primary/5' : ''}`}
                >
                  <button
                    onClick={() => toggleTeamCollapsed(key)}
                    className="flex items-center gap-2 px-2 pt-2 pb-1 w-full text-left hover:bg-base-300/40 rounded-lg transition-colors"
                    title={collapsed ? `Expand ${name}` : `Collapse ${name}`}
                  >
                    <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-base-content/50 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    <span
                      className="w-2 h-2 rounded-full shrink-0 border border-base-content/20"
                      style={{ backgroundColor: color ?? 'transparent' }}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-base-content/70 truncate">{name}</span>
                    <span className="text-[10px] text-base-content/40">({count})</span>
                  </button>
                  {!collapsed && (
                    <div className="pl-2">
                      {renderActiveInactive(bucket.active, bucket.inactive, { indent: true })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {projects.length === 0 && (
          <div className="text-center py-8 px-4">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-xs text-base-content/60">No projects yet</p>
            <p className="text-xs text-base-content/50 mt-1">Create your first project to get started</p>
          </div>
        )}

      </div>

      {/* Right-click: move a project to a team. */}
      {menu && (
        <TeamContextMenu
          x={menu.x}
          y={menu.y}
          currentTeamId={getProjectGroupId(projects.find((p) => p.id === menu.projectId)?.settings ?? null)}
          groups={groups}
          onPick={(teamId) => { void assignToTeam(menu.projectId, teamId); setMenu(null); }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
